import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useHomeInteractions from '../utils/HomeInteractions';
import Slider from '../ui/Slider';
import ReCAPTCHA from 'react-google-recaptcha';
import './Home.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const Home = () => {
  // References for the sliders
  const whatsappSliderRef = useRef(null);
  const industrySliderRef = useRef(null);

  // For navigation
  const navigate = useNavigate();

  // State for feature selection
  const [activeFeature, setActiveFeature] = useState(() => {
    // Initialize from session storage if available
    return sessionStorage.getItem('selectedFeature') || 'mobile-ux';
  });

  // State to track if user manually selected a feature
  const [userSelected, setUserSelected] = useState(false);

  // Auto-cycling feature
  useEffect(() => {
    // Define the feature IDs in order
    const features = [
      'mobile-ux',
      'ai-lead-capture',
      'integrations',
      'chat-builder',
      'omnichannel',
    ];

    // Only auto-cycle if user hasn't manually selected a feature
    if (!userSelected) {
      const interval = setInterval(() => {
        // Find current index
        const currentIndex = features.indexOf(activeFeature);
        // Calculate next index (loop back to 0 if at the end)
        const nextIndex = (currentIndex + 1) % features.length;
        // Set the next feature
        setActiveFeature(features[nextIndex]);
        // Save to session storage
        sessionStorage.setItem('selectedFeature', features[nextIndex]);
      }, 5000); // Cycle every 5 seconds

      // Clean up interval on unmount
      return () => clearInterval(interval);
    }

    // If user selected a feature, set a timeout to resume auto-cycling after 10 seconds
    if (userSelected) {
      const timeout = setTimeout(() => {
        setUserSelected(false);
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [activeFeature, userSelected]);

  // Ref for the URL input
  const urlInputRef = useRef(null);
  const [urlError, setUrlError] = useState(false);

  // Form dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [fullName, setFullName] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [domain, setDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for form submission

  // Function to handle demo button click with URL validation
  const handleDemoClick = (e) => {
    e.preventDefault();

    // Get the input value
    const urlInput = urlInputRef.current;
    if (urlInput) {
      // Trim the URL and remove trailing slashes
      let url = urlInput.value.trim();
      // Remove trailing slash if it exists (unless it's just a single slash path)
      if (url.length > 1 && url.endsWith('/')) {
        url = url.replace(/\/*$/, ''); // Remove all trailing slashes
      }

      // URL validation regex that accepts domains and paths
      // This accepts:
      // - Standard domains (example.com)
      // - Domains with subdomains (sub.example.com)
      // - Domains with paths (example.com/path)
      // - Paths only (/path/to/resource)
      const domainRegex = /^((?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}(\/.*)*/;
      const pathOnlyRegex = /^\/[A-Za-z0-9\/_.-]*$/;

      if (!url || !(domainRegex.test(url) || pathOnlyRegex.test(url))) {
        // Invalid URL - show error and vibrate input
        setUrlError(true);

        // Add vibration animation
        urlInput.classList.add('vibrate');

        // Remove vibration class after animation completes
        setTimeout(() => {
          urlInput.classList.remove('vibrate');
        }, 500);

        return;
      }

      // Valid URL - clear any previous error
      setUrlError(false);

      // Update the input value with the cleaned URL (without trailing slashes)
      urlInput.value = url;

      // Extract and store the domain
      setDomain(url);

      // Open the form dialog instead of navigating
      setShowFormDialog(true);
      document.body.classList.add('body-no-click'); // Add class to prevent background scrolling
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);

    // Add delayed reveal for hero text elements
    const aiChatbotText = document.getElementById('ai-chatbot-text');
    const fiveMinText = document.getElementById('five-min-text');

    // Initially hide the text elements
    if (aiChatbotText) aiChatbotText.style.opacity = '0';
    if (fiveMinText) fiveMinText.style.opacity = '0';

    // Reveal with delay
    setTimeout(() => {
      if (aiChatbotText) {
        aiChatbotText.style.opacity = '1';
        aiChatbotText.style.transform = 'translateY(0)';
      }
    }, 800); // Delay for AI Chatbot text

    setTimeout(() => {
      if (fiveMinText) {
        fiveMinText.style.opacity = '1';
        fiveMinText.style.transform = 'translateY(0)';
      }
    }, 1600); // Delay for 5 Min! text
  }, []);

  // Animation for glow elements with configurable timing
  useEffect(() => {
    // Animation configuration - define which elements to animate and their timing
    const animationConfig = [
      // Format: [selector, delay in ms]
      // Left side elements - align with left SVG paths
      // Left side elements - faster timing
      ['.web-circle', 1000],
      ['.meta-circle', 1300],
      ['.chat-circle', 1600],
      ['.wb-circle', 1900],
      ['.sponser-circle', 2200],
      ['.social-circle', 2500],
      ['.insta-circle', 2800],
      ['.fb-circle', 3100],
      ['.aimodel-circle', 3400],
      // Right side elements with faster timing
      ['.inner-card', 6700],
      ['.inner-card-img', 6700],
      ['.integration-circle', 8000],
      ['.email-circle', 8700],
      ['.hubspot-circle', 9400],
      ['.zoho-circle', 10100],
      ['.slack-circle', 10800],
      ['.zappiper-circle', 11500], // After all SVG animations

      // Rectangles - appear after most circles

      // Center card - appears after initial elements
    ];

    // Store all timeouts to clear them on unmount
    const timeouts = [];

    // Function to show an element with a specific selector
    const showElement = (selector, delay) => {
      const timeout = setTimeout(() => {
        const element = document.querySelector(selector);
        if (element) {
          element.classList.add('glow-element-visible');
        }
      }, delay);

      timeouts.push(timeout);
    };

    // Schedule all animations
    animationConfig.forEach(([selector, delay]) => {
      showElement(selector, delay);
    });

    // Cleanup timeouts on component unmount
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  // Initialize all the interactive behaviors
  useHomeInteractions();

  // Handle page visibility changes to ensure proper feature initialization
  useEffect(() => {
    // Function to initialize the correct feature
    const initializeFeature = () => {
      // Get the selected feature from session storage
      const savedFeature = sessionStorage.getItem('selectedFeature');
      if (savedFeature) {
        setActiveFeature(savedFeature);
      } else {
        // If no feature is saved, set the default and save it
        setActiveFeature('mobile-ux');
        sessionStorage.setItem('selectedFeature', 'mobile-ux');
      }

      // Force immediate application of styles
      setTimeout(() => {
        // Add preload class to prevent flashing during initialization
        const contentWrapper = document.querySelector('.feature-content-wrapper');
        if (contentWrapper) {
          contentWrapper.classList.add('preload');

          // Remove preload class after a short delay to allow animations
          setTimeout(() => {
            contentWrapper.classList.remove('preload');
          }, 50);
        }
      }, 0);
    };

    // Initialize on component mount
    initializeFeature();

    // Function to handle when page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        initializeFeature();
      }
    };

    // Function to handle when page is shown (e.g., when navigating back)
    const handlePageShow = (e) => {
      // Check if page is loaded from cache (bfcache)
      if (e.persisted) {
        initializeFeature();
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    // Clean up event listeners on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Handle full name input change
  const handleFullNameChange = (value) => {
    setFullName(value);
    if (!value.trim()) {
      setFullNameError('Full name is required');
    } else if (value.trim().length < 3) {
      setFullNameError('Full name must be at least 3 characters');
    } else {
      setFullNameError('');
    }
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Clear previous error first
    setEmailError('');

    // Then validate
    if (!value.trim()) {
      setEmailError('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Please enter a valid email address');
    }
  };

  // Handle reCAPTCHA verification
  const handleRecaptchaVerify = (token) => {
    setRecaptchaVerified(!!token);
    if (token) {
      setSuccessMessage('reCAPTCHA verified successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  // Handle form submission
  const handleFormSubmit = async () => {
    // Reset all error messages first
    setFullNameError('');
    setEmailError('');
    setSuccessMessage('');

    // Flag to track validation errors
    let hasErrors = false;

    // Get references to the input fields
    const fullNameInput = document.querySelector('input[value="' + fullName + '"]');
    const emailInput = document.querySelector('input[value="' + email + '"]');

    // Validate full name
    if (!fullName.trim()) {
      setFullNameError('Full name is required');
      hasErrors = true;

      // Add vibration animation to the full name input
      if (fullNameInput) {
        fullNameInput.classList.add('vibrate');
        setTimeout(() => {
          fullNameInput.classList.remove('vibrate');
        }, 500);
      }
    }

    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      hasErrors = true;

      // Add vibration animation to the email input
      if (emailInput) {
        emailInput.classList.add('vibrate');
        setTimeout(() => {
          emailInput.classList.remove('vibrate');
        }, 500);
      }
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      hasErrors = true;

      // Add vibration animation to the email input
      if (emailInput) {
        emailInput.classList.add('vibrate');
        setTimeout(() => {
          emailInput.classList.remove('vibrate');
        }, 500);
      }
    }

    // Validate reCAPTCHA
    if (!recaptchaVerified) {
      setSuccessMessage('Please complete the reCAPTCHA verification');
      hasErrors = true;

      // Add vibration animation to the reCAPTCHA container
      const recaptchaContainer = document.querySelector('.w-100 .g-recaptcha');
      if (recaptchaContainer) {
        recaptchaContainer.classList.add('vibrate');
        setTimeout(() => {
          recaptchaContainer.classList.remove('vibrate');
        }, 500);
      }
    }

    // If there are any validation errors, stop form submission
    if (hasErrors) {
      return;
    }

    // Set loading state to true
    setIsSubmitting(true);

    // Form is valid, show success message
    // setSuccessMessage('Form submitted successfully!');

    // Post registration data to API
    await postRegistrationData();

    // Clear form after successful submission
    setTimeout(() => {
      setFullName('');
      setEmail('');
      setSuccessMessage('');
      setShowFormDialog(false);
      document.body.classList.remove('body-no-click');
    }, 3000);
  };

  // API call to submit the form data
  const postRegistrationData = async () => {
    try {
      const response = await fetch(`https://admin.aichatassist.com/api/pre-registers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            Full_Name: fullName,
            Email: email,
            Company_Website: domain,
          },
        }),
      });

      // Show success message
      setSuccessMessage('Form submitted successfully! Redirecting to demo...');

      // Redirect to demo page with the domain as URL parameter
      setTimeout(() => {
        navigate(`/demo?url=${encodeURIComponent(domain)}`);
      }, 2000); // Redirect after 2 seconds to show the success message
    } catch (error) {
      console.error('Error posting registration data:', error);
      setSuccessMessage('An error occurred. Please try again.');
      // Reset loading state on error
      setIsSubmitting(false);
    }
  };

  // Counter animation for metrics section
  useEffect(() => {
    // Function to animate counter
    const animateCounter = (elementId, start, end, duration, suffix) => {
      const element = document.getElementById(elementId);
      if (!element) return;

      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        element.textContent = `${value}${suffix}`;

        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    };

    // Function to check if element is in viewport
    const isElementInViewport = (el) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    };

    // Function to start animations when metrics section comes into view
    const handleScroll = () => {
      const metricsSection = document.querySelector('.metrics-section');
      if (metricsSection && isElementInViewport(metricsSection)) {
        // Start animations
        animateCounter('conversations-counter', 0, 100, 2000, 'K+');
        animateCounter('hours-counter', 0, 5, 2000, 'M+');
        animateCounter('conversions-counter', 0, 30, 2000, '%');
        animateCounter('interactions-counter', 0, 2, 2000, 'X');

        // Remove scroll listener once animations have started
        window.removeEventListener('scroll', handleScroll);
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Check if metrics section is already in viewport on load
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Add animation effects to slider cards on load
  // useEffect(() => {
  //   // Function to add animation classes to cards
  //   const animateCards = () => {
  //     // Get all slider cards
  //     const whatsappCards = document.querySelectorAll('.whatsapp-card');
  //     const industryCards = document.querySelectorAll('.industry-card');

  //     // Add animation class to each card with a delay
  //     [...whatsappCards, ...industryCards].forEach((card, index) => {
  //       // Reset any existing animations
  //       card.style.animation = 'none';
  //       card.offsetHeight; // Trigger reflow

  //       // Apply the animation with proper delay
  //       card.style.animation = `fadeInUp 0.8s cubic-bezier(0.19, 1, 0.22, 1) ${index * 0.1}s forwards`;
  //       card.style.opacity = '0';
  //     });
  //   };

  //   // Run the animation when the component mounts
  //   // animateCards();

  //   // Also run the animation when sliders become visible in viewport
  //   const handleScroll = () => {
  //     const whatsappContainer = document.querySelector('.whatsapp-slider-container');
  //     const industryContainer = document.querySelector('.industry-slider-container');

  //     if (whatsappContainer && isElementInViewport(whatsappContainer)) {
  //       const whatsappCards = document.querySelectorAll('.whatsapp-card');
  //       [...whatsappCards].forEach((card, index) => {
  //         card.style.animation = 'none';
  //         card.offsetHeight; // Trigger reflow
  //         card.style.animation = `fadeInUp 0.8s cubic-bezier(0.19, 1, 0.22, 1) ${index * 0.1}s forwards`;
  //         card.style.opacity = '0';
  //       });
  //     }

  //     if (industryContainer && isElementInViewport(industryContainer)) {
  //       const industryCards = document.querySelectorAll('.industry-card');
  //       [...industryCards].forEach((card, index) => {
  //         card.style.animation = 'none';
  //         card.offsetHeight; // Trigger reflow
  //         card.style.animation = `fadeInUp 0.8s cubic-bezier(0.19, 1, 0.22, 1) ${index * 0.1}s forwards`;
  //         card.style.opacity = '0';
  //       });
  //     }
  //   };

  //   // Helper function to check if element is in viewport
  //   const isElementInViewport = (el) => {
  //     const rect = el.getBoundingClientRect();
  //     return (
  //       rect.top >= 0 &&
  //       rect.left >= 0 &&
  //       rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
  //       rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  //     );
  //   };

  //   // Add scroll listener with debounce for better performance
  //   let scrollTimeout;
  //   // const debouncedScroll = () => {
  //   //   clearTimeout(scrollTimeout);
  //   //   scrollTimeout = setTimeout(handleScroll, 100);
  //   // };

  //   window.addEventListener('scroll', debouncedScroll);

  //   // Cleanup
  //   return () => {
  //     window.removeEventListener('scroll', debouncedScroll);
  //     clearTimeout(scrollTimeout);
  //   };
  // }, []);

  // Add a window resize handler to reset slider positions
  useEffect(() => {
    const handleResize = () => {
      // Reset WhatsApp slider position
      const whatsappSlider = document.getElementById('whatsappSlider');
      if (whatsappSlider) {
        whatsappSlider.style.transition = 'none';
        whatsappSlider.style.transform = 'translateX(0)';
      }

      // Reset Industry slider position
      const industrySlider = document.getElementById('industrySlider');
      if (industrySlider) {
        industrySlider.style.transition = 'none';
        industrySlider.style.transform = 'translateX(0)';
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Set up slider navigation
  useEffect(() => {
    // No slider navigation code needed as arrow buttons have been removed
    return () => {
      // Cleanup function
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Best Artificial Intelligence Chatbot Solution for Websites and WhatsApp</title>
        <meta
          name="description"
          content="Integrate AI Chatbot solution with WhatsApp Business as your AI Campaign Manager for 24/7 qualified lead generation."
        />
        <link rel="canonical" href="https://www.aichatassist.com/" />

        {/* Open Graph Meta */}
        <meta
          property="og:title"
          content="AI Chat Assist | Artificial Intelligence Chatbot Solution for Your Website"
        />
        <meta
          property="og:description"
          content="Engage visitors with AI chatbot solutions. Transform your website using AI chat, boosting user interaction and online engagement. Try our AI bot today."
        />
        <meta property="og:url" content="https://www.aichatassist.com/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Meta */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@aichatassist" />
        <meta name="twitter:creator" content="@aichatassist" />
        <meta
          name="twitter:title"
          content="AI Chat Assist | Artificial Intelligence Chatbot Solution for Your Website"
        />
        <meta
          name="twitter:description"
          content="Engage visitors with AI chatbot solutions. Transform your website using AI chat, boosting user interaction and online engagement. Try our AI bot today."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:image:alt" content="AI Chat Assist platform screenshot" />
      </Helmet>

      <section id="home" className="py-5 mt-4 home-page">
        <div className="container text-center">
          <div className="cta-badge-container">
            <div className="cta-badge d-inline-block">Get started free – no credit card needed</div>
          </div>
          <h1 className="hero-heading mb-4 animate-up">
            <span className="underline1">
              Integrate
              <img
                src="images/herosection.png"
                className="hero-background-image"
                alt="Hero Section Image"
              />
            </span>
            <span className="hero-highlight hero-text-reveal" id="ai-chatbot-text">
              AI Chatbot
            </span>
            <br />
            in just{' '}
            <span className="hero-highlight hero-text-reveal" id="five-min-text">
              5 Min!
            </span>
          </h1>
          <p className="description mx-auto">
            AI Chatbot Solution for 24/7 support on your website and WhatsApp—boost engagement, cut
            costs, and qualify leads.
          </p>
          <div className="hero-buttons-container">
            <a
              href="https://app.aichatassist.com/auth/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary-gradient"
            >
              Try Free Trial Today!
            </a>
            <a
              href="https://www.youtube.com/watch?v=2Wqgc5YN1mc"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-circle"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                className="bi bi-play-fill"
                viewBox="0 0 16 16"
              >
                <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
              </svg>
            </a>
            <span className="d-md-inline fw-medium">View Demo</span>
          </div>

          <div className="hero-image-container" style={{ position: 'relative' }}>
            {/* Mobile and tablet view - only visible on screens smaller than 992px */}
            {/* <img
              src="images/process.svg"
              className="img-fluid w-100 mobile-only"
              alt="AI Chatbot Interface"
            /> */}

            {/* Desktop view - only visible on screens larger than or equal to 992px */}
            <div className="desktop-only">
              <div className="glow-circle web-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/web.png" alt="Web" />
              </div>
              <div className="glow-circle chat-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/chat.png" alt="Chat" />
              </div>
              <div className="glow-circle meta-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/meta.png" alt="Meta" />
              </div>
              <div className="glow-circle wb-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/whatsapp.png" alt="WhatsApp" />
              </div>
              <div className="glow-circle insta-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/insta.png" alt="Instagram" />
              </div>
              <div className="glow-circle fb-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/messagnger.png" alt="Facebook Messenger" />
              </div>
              <div className="glow-circle email-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/email.png" alt="Email" />
              </div>
              <div className="glow-circle hubspot-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/hubspot.png" alt="HubSpot" />
              </div>
              <div className="glow-circle zoho-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/zoho.png" alt="Zoho" />
              </div>
              <div className="glow-circle slack-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/slack.png" alt="Slack" />
              </div>
              <div className="glow-circle zappiper-circle" style={{ padding: '0.5rem' }}>
                <img src="/images/animationhome/zapier.png" alt="Zapier" />
              </div>

              <div className="glow-rectangle sponser-circle">
                <div className="icon-wrapper">
                  <img src="/images/animationhome/broadcasting.png" alt="Broadcasting" />
                </div>
                <span>B2C Ads</span>
              </div>

              <div className="glow-rectangle social-circle">
                <div className="icon-wrapper">
                  <img src="/images/animationhome/socialmedia.png  " alt="Social Media" />
                </div>
                <span>Social Media</span>
              </div>

              <div className="glow-rectangle aimodel-circle">
                <div className="icon-wrapper">
                  <img src="/images/animationhome/aimodel.svg" alt="AI Model" />
                </div>
                <span>AI Model</span>
              </div>

              <div className="glow-rectangle integration-circle">
                <div className="icon-wrapper">
                  <img src="/images/animationhome/integration.png" alt="Integration" />
                </div>
                <span>Integration</span>
              </div>

              <img
                className="inner-card-img"
                src="/images/animationhome/innercarddb.png"
                alt="Inner Card"
              />

              <div className="inner-card">
                {/* <!-- Row 1 --> */}
                <div className="scroll-row">
                  <div className="scroll-track">
                    {/* <!-- 20 avatars total --> */}
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/1.jpg" alt="User Avatar" />
                      <span>Floyd Miles</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/2.jpg"
                        alt="User Avatar"
                      />
                      <span>Eleanor Pena</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/3.jpg" alt="User Avatar" />
                      <span>Guy Hawkins</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/4.jpg"
                        alt="User Avatar"
                      />
                      <span>Eleanor</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/5.jpg"
                        alt="User Avatar"
                      />
                      <span>Jane Cooper</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/6.jpg" alt="User Avatar" />
                      <span>Albert Flores</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/7.jpg" alt="User Avatar" />
                      <span>Robert Fox</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/8.jpg" alt="User Avatar" />
                      <span>Cody Fisher</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/9.jpg" alt="User Avatar" />
                      <span>Ravi Kumar</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/10.jpg"
                        alt="User Avatar"
                      />
                      <span>Leena Joshi</span>
                    </div>
                    {/* <!-- Duplicate --> */}
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/1.jpg" alt="User Avatar" />
                      <span>Floyd Miles</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/2.jpg"
                        alt="User Avatar"
                      />
                      <span>Eleanor Pena</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/3.jpg" alt="User Avatar" />
                      <span>Guy Hawkins</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/4.jpg"
                        alt="User Avatar"
                      />
                      <span>Eleanor</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/5.jpg"
                        alt="User Avatar"
                      />
                      <span>Jane Cooper</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/6.jpg" alt="User Avatar" />
                      <span>Albert Flores</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/7.jpg" alt="User Avatar" />
                      <span>Robert Fox</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/8.jpg" alt="User Avatar" />
                      <span>Cody Fisher</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/9.jpg" alt="User Avatar" />
                      <span>Ravi Kumar</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/10.jpg"
                        alt="User Avatar"
                      />
                      <span>Leena Joshi</span>
                    </div>
                  </div>
                </div>

                {/* <!-- Row 2 --> */}
                <div className="scroll-row">
                  <div className="scroll-track reverse">
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/11.jpg" alt="User Avatar" />
                      <span>Jerome Bell</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/12.jpg"
                        alt="User Avatar"
                      />
                      <span>Devon Lane</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/13.jpg" alt="User Avatar" />
                      <span>Jacob Jones</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/14.jpg"
                        alt="User Avatar"
                      />
                      <span>Aliya Baig</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/15.jpg"
                        alt="User Avatar"
                      />
                      <span>Sana Shaikh</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/16.jpg" alt="User Avatar" />
                      <span>Krish Mehta</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/17.jpg"
                        alt="User Avatar"
                      />
                      <span>Eva Mendes</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/18.jpg" alt="User Avatar" />
                      <span>Raj Patil</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/19.jpg"
                        alt="User Avatar"
                      />
                      <span>Neha Rao</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/20.jpg" alt="User Avatar" />
                      <span>Tom Singh</span>
                    </div>
                    {/* <!-- Repeat --> */}
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/11.jpg" alt="User Avatar" />
                      <span>Jerome Bell</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/12.jpg"
                        alt="User Avatar"
                      />
                      <span>Devon Lane</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/13.jpg" alt="User Avatar" />
                      <span>Jacob Jones</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/14.jpg"
                        alt="User Avatar"
                      />
                      <span>Aliya Baig</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/15.jpg"
                        alt="User Avatar"
                      />
                      <span>Sana Shaikh</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/16.jpg" alt="User Avatar" />
                      <span>Krish Mehta</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/17.jpg"
                        alt="User Avatar"
                      />
                      <span>Eva Mendes</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/18.jpg" alt="User Avatar" />
                      <span>Raj Patil</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/19.jpg"
                        alt="User Avatar"
                      />
                      <span>Neha Rao</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/20.jpg" alt="User Avatar" />
                      <span>Tom Singh</span>
                    </div>
                  </div>
                </div>

                {/* <!-- ✅ Row 3 (Now equalized and complete) --> */}
                <div className="scroll-row">
                  <div className="scroll-track">
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/21.jpg" alt="User Avatar" />
                      <span>Sam Shah</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/22.jpg" alt="User Avatar" />
                      <span>Amit Joshi</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/23.jpg"
                        alt="User Avatar"
                      />
                      <span>Ritika Desai</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/24.jpg"
                        alt="User Avatar"
                      />
                      <span>Lisa Ray</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/25.jpg" alt="User Avatar" />
                      <span>Nikhil Verma</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/26.jpg"
                        alt="User Avatar"
                      />
                      <span>Reema Jain</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/27.jpg" alt="User Avatar" />
                      <span>Kabir Khan</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/28.jpg" alt="User Avatar" />
                      <span>Vivek Malhotra</span>
                    </div>
                    <div className="user-avatar">
                      <img
                        src="https://randomuser.me/api/portraits/women/29.jpg"
                        alt="User Avatar"
                      />
                      <span>Shruti Singh</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/30.jpg" alt="User Avatar" />
                      <span>Arjun Roy</span>
                    </div>
                    {/* <!-- Repeat --> */}
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/21.jpg" alt="User Avatar" />
                      <span>Sam Shah</span>
                    </div>
                    <div className="user-avatar">
                      <img src="https://randomuser.me/api/portraits/men/22.jpg" alt="User Avatar" />
                      <span>Amit Joshi</span>
                    </div>
                    {/* <!-- ... --> */}
                  </div>
                </div>
              </div>
            </div>

            {/* SVG animation - only visible on desktop */}
            <svg
              className="desktop-only"
              style={{ width: '100%', height: '400px' }}
              viewBox="0 0 1268 357"
              fill="none"
              preserveAspectRatio="xMinYMin meet"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* <!-- Left Side Lines --> */}
              <g transform="translate(0,10)">
                <path
                  id="line1"
                  d="M2 1L320.87 3.15715C360.692 3.42657 398.207 21.491 422.73 52.2067L508 159"
                  stroke="url(#paint0)"
                  strokeWidth="2"
                />
                <path
                  id="line2"
                  className="delay"
                  d="M322 313C360.559 313 397.053 295.532 421.306 265.468L508 158"
                  stroke="url(#paint1)"
                  strokeWidth="2"
                />
                <path
                  id="line3"
                  className="animate-reverse"
                  d="M508.101 159L29 159.804"
                  stroke="url(#paint0)"
                  strokeWidth="2"
                />
                <path
                  id="line4"
                  className="animate-reverse"
                  d="M323.944 313.007L281.081 280.705C274.438 275.698 266.365 272.952 258.045 272.874L0.259766 270.418"
                  stroke="url(#paint0)"
                  strokeWidth="2"
                />
                <path
                  id="line5"
                  className="animate-reverse"
                  d="M325.431 313.157L283.086 345.36C276.42 350.432 268.295 353.214 259.918 353.294L4.66602 355.747"
                  stroke="url(#paint0)"
                  strokeWidth="2"
                />

                {/* <!-- Animated Circles --> */}
                <circle>
                  <animateMotion dur="2.5s" begin="2.5s" repeatCount="indefinite">
                    <mpath xlinkHref="#line1" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion dur="2.5s" begin="4.5s" repeatCount="indefinite">
                    <mpath xlinkHref="#line2" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="4.0s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#line3" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="3.0s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#line4" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="3.5s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#line5" />
                  </animateMotion>
                </circle>
              </g>

              {/* <!-- Right Side Group with Fade-In --> */}
              <g transform="translate(759,0)" className="delayed-fade">
                <path
                  id="rline5"
                  className="animate-reverse delay1"
                  d="M163 169.247L-255 168"
                  stroke="#804DFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  id="rline1"
                  className="animate-reverse delay2"
                  d="M517.743 1.1748H278.782C250.153 1.1748 226.946 24.3697 226.946 52.982V130.693C226.946 152.152 209.54 169.548 188.069 169.548H171.738"
                  stroke="url(#paint2)"
                  strokeWidth="2"
                />
                <path
                  id="rline2"
                  className="animate-reverse delay2"
                  d="M517.743 85.3604H275.078C248.494 85.3604 226.946 106.898 226.946 133.467C226.946 153.394 210.784 169.547 190.845 169.547H171.738"
                  stroke="url(#paint2)"
                  strokeWidth="2"
                />
                <path
                  id="rline3"
                  className="animate-reverse delay2"
                  d="M517.743 337.144H278.782C250.153 337.144 226.946 313.95 226.946 285.337V207.626C226.946 186.167 209.54 168.771 188.069 168.771H171.738"
                  stroke="url(#paint2)"
                  strokeWidth="2"
                />
                <path
                  id="rline4"
                  className="animate-reverse delay2"
                  d="M517.743 252.528H274.832C248.385 252.528 226.946 231.1 226.946 204.667C226.946 184.843 210.867 168.772 191.032 168.772H171.738"
                  stroke="url(#paint2)"
                  strokeWidth="2"
                />
                <path
                  id="rline6"
                  className="animate-reverse delay2"
                  d="M510 170.247L165 169"
                  stroke="url(#paint2)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />

                {/* <!-- Right Circles --> */}
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="8.5s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#rline1" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="8.0s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#rline2" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="9.0s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#rline3" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="9.5s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#rline4" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="6s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#rline5" />
                  </animateMotion>
                </circle>
                <circle>
                  <animateMotion
                    dur="2.5s"
                    begin="9.5s"
                    repeatCount="indefinite"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath xlinkHref="#rline6" />
                  </animateMotion>
                </circle>
              </g>

              {/* <!-- Gradients --> */}
              <defs>
                <linearGradient
                  id="paint0"
                  x1="0"
                  y1="0"
                  x2="700"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#F5F4FE" />
                  <stop offset="1" stopColor="#804CFF" />
                </linearGradient>
                <linearGradient
                  id="paint1"
                  x1="0"
                  y1="0"
                  x2="700"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#7762FF" />
                  <stop offset="1" stopColor="#804CFF" />
                </linearGradient>
                <linearGradient
                  id="paint2"
                  x1="0"
                  y1="0"
                  x2="700"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#804CFF" />
                  <stop offset="1" stopColor="#FFFFFF" />
                </linearGradient>
                <linearGradient
                  id="paint3"
                  x1="0"
                  y1="0"
                  x2="700"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#804CFF" />
                  <stop offset="1" stopColor="#FDFDFF" />
                </linearGradient>
                <linearGradient
                  id="paint4"
                  x1="0"
                  y1="0"
                  x2="700"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#804CFF" />
                  <stop offset="1" stopColor="#FEFEFF" />
                </linearGradient>
              </defs>
            </svg>
            {/* <img src="images/process.svg" className="img-fluid w-100" alt="AI Chatbot Interface" /> */}
          </div>
        </div>
      </section>

      <section className="py-5 partners-section">
        <div className="text-center mb-4">
          <p className="text-muted">Trusted by innovative companies</p>
        </div>
        <div className="partners-container">
          <div className="partners-scroll">
            <div className="partners-track">
              <div className="partner-item">
                <img
                  src="images/brands/svi.png"
                  className="partner-logo"
                  alt="Swami Vivekanand International School Mumbai"
                />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/absulute.png"
                  className="partner-logo"
                  alt="Absolute Foundation"
                />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/bliss.png"
                  className="partner-logo"
                  alt="Bliss International School Pune"
                />
              </div>
              <div className="partner-item">
                <img src="images/brands/dsk.png" className="partner-logo" alt="DSK School Pune" />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/japalouppe.png"
                  className="partner-logo"
                  alt="Japalouppe Equestrian Centre"
                />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/jnanaprabodhini.jpg"
                  className="partner-logo"
                  alt="Jnanaprabodhini School Pune"
                />
              </div>
              <div className="partner-item">
                <img src="images/brands/mantra.png" className="partner-logo" alt="Mantra Resort" />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/abbhyas.png"
                  className="partner-logo"
                  alt="Abhyaas Tutorials"
                />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/svi.png"
                  className="partner-logo"
                  alt="Swami Vivekanand International School Mumbai"
                />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/absulute.png"
                  className="partner-logo"
                  alt="Absolute Foundation"
                />
              </div>
              <div className="partner-item">
                <img
                  src="images/brands/almughamer.png"
                  className="partner-logo"
                  alt="Al Mughamer"
                />
              </div>
              <div className="partner-item">
                <img src="images/brands/core.png" className="partner-logo" alt="Core" />
              </div>
              <div className="partner-item">
                <img src="images/brands/deepauth.png" className="partner-logo" alt="DeepAuth" />
              </div>
              <div className="partner-item">
                <img src="images/brands/ltrek.png" className="partner-logo" alt="L-Trek" />
              </div>
              <div className="partner-item">
                <img src="images/brands/ninsmart.png" className="partner-logo" alt="NinSmart" />
              </div>
              <div className="partner-item">
                <img src="images/brands/vtrek.png" className="partner-logo" alt="V-Trek" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="py-5">
        <div className="container demo-container">
          <img
            src="images/chatbot.png"
            className="img-fluid d-none d-md-block d-xl-block animate-up chatbot-demo-image ms-md-4"
            alt="Chatbot Mobile Demo"
          />
          <div className="card demo-card ms-md-auto">
            <div className="card-body demo-card-body">
              <div className="row align-items-center">
                <div className="col-md-5 mb-4 mb-md-0 text-center position-relative"></div>

                <div className="col-md-7 hero-cta-section">
                  <h5 className="text-white typing-text">Create Free Demo</h5>
                  <h2 className="text-white typing-text">
                    <span>Try it now</span>
                    <br />
                    on your website instantly
                  </h2>
                  <p className="text-white typing-delay">
                    Just enter your website URL—we'll instantly create a custom AI chatbot demo
                    using our Artificial Intelligence Chatbot platform.
                  </p>

                  <div className="d-flex flex-wrap mb-1" id="feature-badges">
                    <span className="feature-badge text-white">
                      <div className="checkbox-container">
                        <label className="custom-checkbox">
                          <input type="checkbox" disabled className="feature-checkbox" />
                          <span className="checkbox-square"></span>
                          <span className="checkmark">
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </span>
                        </label>
                      </div>
                      <span className="feature-text">No sign-up</span>
                    </span>
                    <span className="feature-badge text-white">
                      <div className="checkbox-container">
                        <label className="custom-checkbox">
                          <input type="checkbox" disabled className="feature-checkbox" />
                          <span className="checkbox-square"></span>
                          <span className="checkmark">
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </span>
                        </label>
                      </div>
                      <span className="feature-text">No coding</span>
                    </span>
                    <span className="feature-badge text-white">
                      <div className="checkbox-container">
                        <label className="custom-checkbox">
                          <input type="checkbox" disabled className="feature-checkbox" />
                          <span className="checkbox-square"></span>
                          <span className="checkmark">
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </span>
                        </label>
                      </div>
                      <span className="feature-text">Real-time experience</span>
                    </span>
                  </div>

                  <div className="url-input-container">
                    <span className="url-prefix">https://</span>
                    <input
                      type="text"
                      className="url-input"
                      placeholder="www.yourwebsite.com"
                      ref={urlInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleDemoClick(e);
                        }
                      }}
                    />
                    <span className="placeholder-text">yourwebsite.com</span>
                    <button className="url-submit-btn" onClick={handleDemoClick}>
                      <span className="arrow">→</span>
                    </button>
                  </div>
                </div>
                {urlError && (
                  <div className="url-error-message">
                    Invalid URL format. Please enter a valid domain.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="section-padding" style={{ paddingBottom: '0px' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">
              <span className="hero-highlight" style={{ fontSize: '4rem' }}>
                The Chatbot
              </span>
              <span className="subtitle">That Actually Converts</span>
              <span className="description">
                More than just live chat — an AI Chatbot for Customer Support that works 24/7 to
                convert visitors into leads
              </span>
            </h2>
          </div>

          <div className="row align-items-center">
            <div className="col-md-3 text-md-end text-center mb-4 mb-md-0">
              <div className="feature-options-container">
                <div
                  className={`feature-option ${activeFeature === 'mobile-ux' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveFeature('mobile-ux');
                    sessionStorage.setItem('selectedFeature', 'mobile-ux');
                    setUserSelected(true);
                  }}
                >
                  Mobile UX
                </div>
                <div
                  className={`feature-option ${
                    activeFeature === 'ai-lead-capture' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setActiveFeature('ai-lead-capture');
                    sessionStorage.setItem('selectedFeature', 'ai-lead-capture');
                    setUserSelected(true);
                  }}
                >
                  Lead Qualification
                </div>
                <div
                  className={`feature-option ${activeFeature === 'integrations' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveFeature('integrations');
                    sessionStorage.setItem('selectedFeature', 'integrations');
                    setUserSelected(true);
                  }}
                >
                  Integrations
                </div>
                <div
                  className={`feature-option ${activeFeature === 'chat-builder' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveFeature('chat-builder');
                    sessionStorage.setItem('selectedFeature', 'chat-builder');
                    setUserSelected(true);
                  }}
                >
                  Chat Builder
                </div>
                <div
                  className={`feature-option ${activeFeature === 'omnichannel' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveFeature('omnichannel');
                    sessionStorage.setItem('selectedFeature', 'omnichannel');
                    setUserSelected(true);
                  }}
                >
                  Omnichannel
                </div>
              </div>
            </div>

            <div
              className="col-md-6 text-center mb-4 mb-md-0 feature-image-wrapper mobile-only-view"
              style={{ position: 'relative', height: '340px', width: '100%' }}
            >
              <img
                src="images/aichatbot.png"
                className="img-fluid parallax-element"
                alt="Parallax Element Mobile"
              />
            </div>

            <div
              className="col-md-6 text-center mb-4 mb-md-0 feature-image-wrapper tablet-desktop-only-view"
              style={{ position: 'relative' }}
            >
              <img
                src="images/featurebg.png"
                className="feature-background"
                alt="Feature Background"
              />
              <div className="feature-content-container parallax-container">
                <div className="parallax-layer" data-depth="0.1">
                  <img
                    src="images/section2feature.png"
                    className="img-fluid feature-image"
                    alt="Feature Image"
                  />
                </div>

                <div className="parallax-layer" data-depth="0.2">
                  <img
                    src="images/parallax/fram0.png"
                    className="img-fluid parallax-element"
                    alt="Parallax Element 1"
                  />
                </div>
                <div className="parallax-layer" data-depth="0.3">
                  <img
                    src="images/parallax/fram1.png"
                    className="img-fluid parallax-element"
                    alt="Parallax Element 1"
                  />
                </div>
                <div className="parallax-layer" data-depth="0.4">
                  <img
                    src="images/parallax/fram2.png"
                    className="img-fluid parallax-element"
                    alt="Parallax Element 1"
                  />
                </div>
                <div className="parallax-layer" data-depth="0.25">
                  <img
                    src="images/parallax/fram3.png"
                    className="img-fluid parallax-element"
                    alt="Parallax Element 1"
                  />
                </div>
                <div className="parallax-layer" data-depth="0.35">
                  <img
                    src="images/parallax/frame4.png"
                    className="img-fluid parallax-element"
                    alt="Parallax Element 1"
                  />
                </div>
                <div className="parallax-layer" data-depth="0.35">
                  <img
                    src="images/parallax/frame5.png"
                    className="img-fluid parallax-element"
                    alt="Parallax Element Desktop"
                  />
                </div>

                {/* Feature content elements using React conditional rendering */}
                <div
                  className={`feature-content d-none d-md-block ${
                    activeFeature === 'mobile-ux' ? 'active' : ''
                  }`}
                ></div>
                <div
                  className={`feature-content d-none d-md-block ${
                    activeFeature === 'ai-lead-capture' ? 'active' : ''
                  }`}
                ></div>
                <div
                  className={`feature-content d-none d-md-block ${
                    activeFeature === 'integrations' ? 'active' : ''
                  }`}
                ></div>
                <div
                  className={`feature-content d-none d-md-block ${
                    activeFeature === 'chat-builder' ? 'active' : ''
                  }`}
                ></div>
                <div
                  className={`feature-content d-none d-md-block ${
                    activeFeature === 'omnichannel' ? 'active' : ''
                  }`}
                ></div>
              </div>
            </div>

            <div className="col-md-3 text-md-start text-center mt-4 mt-md-0">
              {/* Feature content container with fixed height to prevent layout shifts */}
              <div
                className="feature-content-wrapper"
                style={{ minHeight: '150px', position: 'relative', overflow: 'hidden' }}
              >
                {/* Only render the active feature content */}
                {activeFeature === 'mobile-ux' && (
                  <div className="feature-text-content active">
                    <h5 className="fw-bold mb-3 typing-text">Built for Mobile Engagement</h5>
                    <p className="text-muted typing-delay">
                      Deliver fast, native-like mobile experiences with your artificial intelligence
                      chatbot — optimized for any screen.
                    </p>
                  </div>
                )}

                {activeFeature === 'ai-lead-capture' && (
                  <div className="feature-text-content active">
                    <h5 className="fw-bold mb-3 typing-text">AI-Powered Lead Qualification</h5>
                    <p className="text-muted typing-delay">
                      Use AI Chatbots for Customer Support to qualify leads around the clock —
                      gather insights before a human steps in.
                    </p>
                  </div>
                )}

                {activeFeature === 'integrations' && (
                  <div className="feature-text-content active">
                    <h5 className="fw-bold mb-3 typing-text">Effortless Integrations</h5>
                    <p className="text-muted typing-delay">
                      Integrate AI Chatbot solutions with your CRM, email tools, and support
                      platforms—no code, no disruption.
                    </p>
                  </div>
                )}

                {activeFeature === 'chat-builder' && (
                  <div className="feature-text-content active">
                    <h5 className="fw-bold mb-3 typing-text">Visual Chat Flow Designer</h5>
                    <p className="text-muted typing-delay">
                      Build and refine chatbot conversations with our intuitive drag-and-drop AI
                      Chatbot Solution—no technical skills needed.
                    </p>
                  </div>
                )}

                {activeFeature === 'omnichannel' && (
                  <div className="feature-text-content active">
                    <h5 className="fw-bold mb-3 typing-text">One Bot, Multiple Channels</h5>
                    <p className="text-muted typing-delay">
                      Deploy your artificial intelligence bot across websites, mobile apps,
                      WhatsApp, and more—from one simple dashboard.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-4" style={{ paddingTop: '0px !important' }}>
        <div className="full-width-section">
          <div className="metrics-section">
            <div className="container-fluid px-0">
              <div className="row text-center align-items-center justify-content-between g-4 metrics-row">
                <div className="col-6 col-md-6 col-lg-3">
                  <h3 className="fw-bold mb-0" id="conversations-counter">
                    0K+
                  </h3>
                  <small>Conversations</small>
                </div>

                <div className="col-6 col-md-6 col-lg-3">
                  <h3 className="fw-bold mb-0" id="hours-counter">
                    0M+
                  </h3>
                  <small>Hours Saved</small>
                </div>

                <div className="col-6 col-md-6 col-lg-3">
                  <h3 className="fw-bold mb-0" id="conversions-counter">
                    0%
                  </h3>
                  <small>More Conversions</small>
                </div>

                <div className="col-6 col-md-6 col-lg-3">
                  <h3 className="fw-bold mb-0" id="interactions-counter">
                    0X
                  </h3>
                  <small>More Interactions</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 section-padding">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">
              <span className="gradient-text">Conversations</span> That Funnel Users
            </h2>
          </div>

          <div className="row">
            <div className="col-md-5">
              <div className="steps-container">
                <div className="step-item">
                  <div className="step-number-container">
                    <div className="step-number">01</div>
                  </div>
                  <div className="step-content-container">
                    <h6 className="fw-bold">Design High-Converting Conversations</h6>
                    <div className="step-content">
                      <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Quickly build AI Chatbot flows using our drag-and-drop interface. No tech
                        skills required—just pick from 16+ smart blocks like calendars, logic, and
                        multi-choice.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number-container">
                    <div className="step-number step-number-funnel">02</div>
                  </div>
                  <div className="step-content-container">
                    <h6 className="fw-bold">Instantly Capture & Qualify Leads</h6>
                    <div className="step-content">
                      <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Engage users in real-time with an AI Chatbot Solution that captures leads
                        and qualifies them without delay—right when interest peaks.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number-container">
                    <div className="step-number">03</div>
                  </div>
                  <div className="step-content-container">
                    <h6 className="fw-bold">Personalize with AI Precision</h6>
                    <div className="step-content">
                      <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Tailor each conversation using customer behavior and data insights. Your
                        artificial intelligence bot adapts messaging to maximize relevance and
                        results.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number-container">
                    <div className="step-number">04</div>
                  </div>
                  <div className="step-content-container">
                    <h6 className="fw-bold">Automate Like a Pro</h6>
                    <div className="step-content">
                      <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Let your AI Chatbot handle follow-ups, send alerts, and trigger
                        workflows—all based on user actions. Stay responsive even when you're
                        offline.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number-container">
                    <div className="step-number">05</div>
                  </div>
                  <div className="step-content-container">
                    <h6 className="fw-bold">Connect With Any System</h6>
                    <div className="step-content">
                      <p className="text-muted">
                        Integrate with your existing CRM, marketing automation, and customer support
                        tools. Seamlessly connect with your tech stack to maximize efficiency.
                      </p>
                    </div>
                  </div>
                </div>

                {/* <div className="mt-4">
                  <a href="#" className="btn btn-primary-gradient">
                    Try Free Trial Today!
                  </a>
                </div> */}
              </div>
            </div>

            <div className="col-md-7 d-none d-md-block d-xl-block position-relative">
              <img
                src="images/conversion.svg"
                alt="Chat Nodes Example"
                className="img-fluid animate-scale"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="whatsapp" className="py-5">
        <div className="background-shape"></div>
        <div
          className="container"
          style={{ minHeight: '450px', height: 'auto', paddingBottom: '3rem' }}
        >
          <div className="position-relative">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h3 className="hero-heading" style={{ fontWeight: '600', fontSize: '4rem' }}>
                  <span style={{ position: 'relative' }}>
                    Power Your
                    {/* <div className="arrow-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#7a4bff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div> */}
                  </span>{' '}
                  <br />
                  <span className=" hero-highlight">WhatsApp with AI</span>
                </h3>
              </div>
              <div className="col-md-5">
                <p
                  className="text-muted"
                  style={{ color: '#A6A6A6', fontSize: '1rem', fontWeight: '200' }}
                >
                  Integrate an Artificial Intelligence Chatbot with WhatsApp Business to instantly
                  respond to users, qualify leads, and manage chats 24/7. From customer support to
                  conversions, let your AI Chatbots for Customer Support handle it all.
                </p>
              </div>
            </div>

            <div
              className="row text-center g-4 feature-cards-container"
              style={{ marginLeft: '150px' }}
            >
              <div className="col-6 col-md-6 col-lg-3">
                <div className="home-feature-card">
                  <div className="home-feature-image mx-auto">
                    <img src="images/wbsection/seamless.svg" alt="Seamless Setup icon" />
                  </div>
                  <h5 className="home-feature-title fw-semibold">Seamless Setup</h5>
                  <p className="home-feature-description">
                    Integrate your AI chatbot in minutes—no coding needed. Start engaging with
                    customers right away.
                  </p>
                  <Link to="/platform/whatsapp-integration" className="home-cta-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="col-6 col-md-6 col-lg-3">
                <div className="home-feature-card">
                  <div className="home-feature-image mx-auto">
                    <img src="images/wbsection/autometic.svg" alt="Automatic Replies icon" />
                  </div>
                  <h5 className="home-feature-title fw-semibold">Automatic Replies, 24/7</h5>
                  <p className="home-feature-description">
                    Your AI Campaign Manager automates replies and collects qualified leads around
                    the clock.
                  </p>
                  <Link to="/platform/whatsapp-integration" className="home-cta-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="col-6 col-md-6 col-lg-3">
                <div className="home-feature-card">
                  <div className="home-feature-image mx-auto">
                    <img src="images/wbsection/smartmode.svg" alt="Smart Modes icon" />
                  </div>
                  <h5 className="home-feature-title fw-semibold">Smart Modes</h5>
                  <p className="home-feature-description">
                    Tailor your artificial intelligence bot to match business goals with smart,
                    adaptive modes.
                  </p>
                  <Link to="/platform/whatsapp-integration" className="home-cta-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="col-6 col-md-6 col-lg-3">
                <div className="home-feature-card">
                  <div className="home-feature-image mx-auto">
                    <img src="images/wbsection/easyhuman.svg" alt="Easy Human Handoff icon" />
                  </div>
                  <h5 className="home-feature-title fw-semibold">Easy Human Handoff</h5>
                  <p className="home-feature-description">
                    Let AI Chatbots handle the basics, then transfer smoothly to human agents when
                    needed.
                  </p>
                  <Link to="/platform/whatsapp-integration" className="home-cta-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="campaign" className="py-5 section-padding">
        <div className="container text-center">
          <h2 className="section-title mb-1">
            <span
              className="hero-heading hero-highlight"
              style={{ fontWeight: 800, fontSize: '4rem' }}
            >
              AI Campaign Manager
            </span>
          </h2>
          <div className="row justify-content-center">
            <div className="col-md-8">
              <p className="description">
                {' '}
                Use our AI Campaign Manager to launch behavior-based marketing campaigns through
                WhatsApp Business—automated, personalized, and ready to generate qualified leads
                24/7.
              </p>
            </div>
          </div>
          <div className="container-fluid px-0 mt-4">
            <img
              src="images/aicampaign.svg"
              alt="AI Campaign Network"
              className="img-fluid animate-scale w-100"
              style={{ borderRadius: 0 }}
            />
          </div>
          <div className="row justify-content-center mt-5">
            <div className="col-md-8">
              <Link to="/features/ai-campaigns" className="btn btn-primary-gradient mt-3">
                Try AI Campaign Manager Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div
          className="container"
          style={{ minHeight: '450px', height: 'auto', paddingBottom: '3rem' }}
        >
          <div className="position-relative">
            <div className="row align-items-center">
              <div className="col-md-11">
                {/* <p className="subtitle">Features</p> */}
                <h3 className="hero-heading" style={{ fontWeight: '600', fontSize: '4rem' }}>
                  WhatsApp Business
                  <br />
                  <span className="hero-highlight"> Reputation Builder</span>
                </h3>
              </div>
              <div className="col-md-1 d-flex position-relative">
                {/* Arrow buttons removed as requested */}
              </div>
            </div>
          </div>
          <div className="whatsapp-slider-container position-relative">
            <div
              id="whatsappSlider"
              ref={whatsappSliderRef}
              className="d-flex whatsapp-slider"
              style={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'visible',
                position: 'relative',
                gap: '25px',
                transition: 'transform 0.4s ease-out',
              }}
            >
              <Slider
                prevButtonId="prevSlide"
                nextButtonId="nextSlide"
                imageSelector=".whatsapp-card-img"
                // autoSlideDelay={5000}
                autoSlideEnabled={false}
                paddingOffset={20}
                breakpoints={{
                  mobile: 768,
                  tablet: 992,
                }}
              >
                <div className="whatsapp-card">
                  <img
                    src="images/wbrampup/mulitphone.png"
                    alt="Multi-Number Campaigns"
                    className="whatsapp-card-img"
                  />
                  <div className="whatsapp-card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <h4 className="whatsapp-card-title mb-0">
                        Multi-Number Campaigns,
                        <br />
                        Unified AI Dashboard
                      </h4>
                    </div>
                    <p className="card-hover-text mt-3">
                      Manage multiple WhatsApp Business numbers with AI Chatbots—track campaigns
                      from one smart dashboard.
                    </p>
                  </div>
                </div>
                <div className="whatsapp-card">
                  <img
                    src="images/wbrampup/mulitphone.png"
                    alt="Auto Ramp-Up"
                    className="whatsapp-card-img"
                  />
                  <div className="whatsapp-card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <h4 className="whatsapp-card-title mb-0">Auto Ramp-Up with AI Precision</h4>
                    </div>
                    <p className="card-hover-text mt-3">
                      Boost message volume automatically using AI, while maintaining deliverability
                      and trusted sender status.
                    </p>
                  </div>
                </div>
                <div className="whatsapp-card">
                  <img
                    src="images/wbrampup/templates.png"
                    alt="AI-Powered Template Builder"
                    className="whatsapp-card-img"
                  />
                  <div className="whatsapp-card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <h4 className="whatsapp-card-title mb-0">AI Template Builder</h4>
                    </div>
                    <p className="card-hover-text mt-3">
                      Design message templates using Artificial Intelligence Chatbot tools to boost
                      engagement and conversions.
                    </p>
                  </div>
                </div>
                <div className="whatsapp-card">
                  <img
                    src="images/wbrampup/templates.png"
                    alt="Advanced Analytics"
                    className="whatsapp-card-img"
                  />
                  <div className="whatsapp-card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <h4 className="whatsapp-card-title mb-0">Analytics Dashboard</h4>
                    </div>
                    <p className="card-hover-text mt-3">
                      Track message delivery, user engagement, and AI Chatbot results in real-time
                      for smarter decisions.
                    </p>
                  </div>
                </div>
                <div className="whatsapp-card">
                  <img
                    src="images/wbrampup/templates.png"
                    alt="CRM Integration"
                    className="whatsapp-card-img"
                  />
                  <div className="whatsapp-card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <h4 className="whatsapp-card-title mb-0">CRM Integration</h4>
                    </div>
                    <p className="card-hover-text mt-3">
                      Sync your AI Chatbot with your CRM to streamline workflows and keep customer
                      data connected.
                    </p>
                  </div>
                </div>
              </Slider>
            </div>
          </div>
          <div className="text-center mt-5">
            <Link
              to="/platform/whatsapp-integration"
              className="btn text-white px-5 py-3"
              style={{
                background: 'var(--purple-gradient)',
                border: 'none',
                fontWeight: '600',
                borderRadius: '50px',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(140, 111, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              View Whatsapp Integration
            </Link>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div
          className="container"
          style={{ minHeight: '450px', height: 'auto', paddingBottom: '3rem' }}
        >
          <div className="position-relative">
            <div className="row align-items-center">
              <div className="col-md-11">
                {/* <p className="subtitle">Solutions</p> */}
                <h3 className="hero-heading" style={{ fontWeight: '600', fontSize: '4rem' }}>
                  Choose Your <span className="hero-highlight">Industry</span>
                </h3>
              </div>
              <div className="col-md-1 d-flex position-relative">
                {/* Arrow buttons removed as requested */}
              </div>
            </div>
          </div>
          <div className="industry-slider-container position-relative">
            <div
              id="industrySlider"
              ref={industrySliderRef}
              className="d-flex industry-slider"
              style={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'visible',
                position: 'relative',
                gap: '25px',
                transition: 'transform 0.4s ease-out',
              }}
            >
              <Slider
                prevButtonId="prevIndustrySlide"
                nextButtonId="nextIndustrySlide"
                imageSelector=".industry-card-img"
                autoSlideEnabled={false}
                paddingOffset={20}
                breakpoints={{
                  mobile: 768,
                  tablet: 992,
                }}
              >
                <div className="industry-card">
                  <img
                    src="images/industries/retail.png"
                    alt="B2C &amp; D2C Brands"
                    className="industry-card-img"
                  />
                  <div className="industry-card-overlay">
                    <h4 className="industry-card-title">B2C &amp; D2C Brands</h4>
                  </div>
                  <div className="industry-card-body">
                    <h4 className="industry-card-title">B2C &amp; D2C Brands</h4>
                    <p className="industry-hover-text">
                      Use AI Chatbots for Customer Support to offer instant product suggestions,
                      recover carts, and increase conversions—automatically.
                    </p>
                    <div className="card-arrow-icon">
                      <Link to="/industries/b2c-d2c" aria-label="Explore B2C & D2C Solutions">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17l9.2-9.2M17 17V7H7"></path>
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="industry-card">
                  <img
                    src="images/industries/travel.png"
                    alt="Hospitality &amp; Resorts"
                    className="industry-card-img"
                  />
                  <div className="industry-card-overlay">
                    <h4 className="industry-card-title">Hospitality &amp; Resorts</h4>
                  </div>
                  <div className="industry-card-body">
                    <h4 className="industry-card-title">Hospitality &amp; Resorts</h4>
                    <p className="industry-hover-text">
                      Integrate AI Chatbots to handle guest inquiries, bookings, and upsells
                      24/7—boosting satisfaction without added staff.
                    </p>
                    <div className="card-arrow-icon">
                      <Link to="/industries/hospitality" aria-label="Explore Hospitality Solutions">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17l9.2-9.2M17 17V7H7"></path>
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="industry-card">
                  <img
                    src="images/industries/education.png"
                    alt="Education &amp; EdTech"
                    className="industry-card-img"
                  />
                  <div className="industry-card-overlay">
                    <h4 className="industry-card-title">Education &amp; EdTech</h4>
                  </div>
                  <div className="industry-card-body">
                    <h4 className="industry-card-title">Education &amp; EdTech</h4>
                    <p className="industry-hover-text">
                      Use an Artificial Intelligence Chatbot to simplify admissions, automate
                      queries, and keep students and parents updated—effortlessly.
                    </p>
                    <div className="card-arrow-icon">
                      <Link to="/industries/education" aria-label="Explore Education Solutions">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17l9.2-9.2M17 17V7H7"></path>
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="industry-card">
                  <img
                    src="images/industries/healthcare.png"
                    alt="Healthcare &amp; Clinics"
                    className="industry-card-img"
                  />
                  <div className="industry-card-overlay">
                    <h4 className="industry-card-title">Healthcare &amp; Clinics</h4>
                  </div>
                  <div className="industry-card-body">
                    <h4 className="industry-card-title">Healthcare &amp; Clinics</h4>
                    <p className="industry-hover-text">
                      Use AI Chatbots for Customer Support to handle bookings, answer patient
                      queries, and ease receptionist workload.
                    </p>
                    <div className="card-arrow-icon">
                      <a href="#" aria-label="Explore Healthcare Solutions">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17l9.2-9.2M17 17V7H7"></path>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="industry-card">
                  <img
                    src="images/industries/retail.png"
                    alt="Retail &amp; E-commerce"
                    className="industry-card-img"
                  />
                  <div className="industry-card-overlay">
                    <h4 className="industry-card-title">Retail &amp; E-commerce</h4>
                  </div>
                  <div className="industry-card-body">
                    <h4 className="industry-card-title">Retail &amp; E-commerce</h4>
                    <p className="industry-hover-text">
                      Integrate AI Chatbots to provide instant support, suggest products, and track
                      orders—boosting satisfaction at scale.
                    </p>
                    <div className="card-arrow-icon">
                      <a href="#" aria-label="Explore Retail Solutions">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17l9.2-9.2M17 17V7H7"></path>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </Slider>
            </div>
          </div>
        </div>
      </section>

      {/* Form Dialog Popup */}
      {showFormDialog && (
        <div className="popup-overlay">
          <div
            className="popup-dialog"
            style={{
              background: 'linear-gradient(135deg, #a17ffd 0%, #7a4bff 100%)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              color: 'white',
              borderRadius: '15px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              marginTop: '1rem',
              padding: '0px 30px',
            }}
          >
            {/* Cross decorations like in DemoCTA */}
            <span
              style={{
                position: 'absolute',
                bottom: '40px',
                left: '15%',
                fontSize: '20px',
                color: 'white',
                opacity: '0.3',
              }}
            >
              +
            </span>
            <span
              style={{
                position: 'absolute',
                bottom: '30px',
                right: '10%',
                fontSize: '20px',
                color: 'white',
                opacity: '0.3',
              }}
            >
              +
            </span>
            <span
              style={{
                position: 'absolute',
                top: '10%',
                left: '8%',
                fontSize: '20px',
                color: 'white',
                opacity: '0.3',
              }}
            >
              +
            </span>
            <span
              style={{
                position: 'absolute',
                top: '10%',
                right: '8%',
                fontSize: '20px',
                color: 'white',
                opacity: '0.3',
              }}
            >
              +
            </span>
            <span
              className="popup-close"
              onClick={() => {
                setShowFormDialog(false); // Close the dialog
                document.body.classList.remove('body-no-click'); // Remove the class from the body
              }}
              style={{ color: 'white', cursor: 'pointer', marginTop: '1rem', display: 'block' }}
            >
              ✕
            </span>
            <h3
              className="popup-title"
              style={{
                color: 'white',
                fontSize: '2.5rem',
                fontWeight: '200',
                lineHeight: '1.2',
                marginBottom: '30px',
                textAlign: 'center',
              }}
            >
              Create Your Own
              <br />
              <span style={{ fontWeight: '800' }}>AI Chatbot</span> Demo Instantly
            </h3>

            <div className="popup-input-wrapper">
              <label
                className="popup-input-label form-heading-font"
                style={{ color: 'white', marginBottom: '8px', display: 'block' }}
              >
                Full Name
              </label>
              <input
                type="text"
                placeholder="Full Name"
                value={fullName}
                onChange={(e) => handleFullNameChange(e.target.value)}
                className={`popup-input form-heading-font ${fullNameError ? 'error-border' : ''}`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 15px',
                  width: '100%',
                }}
              />
              {fullNameError && (
                <p className="error-message" style={{ color: 'rgb(255 254 254)' }}>
                  {fullNameError}
                </p>
              )}
            </div>

            <div className="popup-input-wrapper">
              <label
                className="popup-input-label form-heading-font"
                style={{ color: 'white', marginBottom: '8px', display: 'block' }}
              >
                Email
              </label>
              <input
                type="email"
                placeholder="Enter your organizational email"
                value={email}
                onChange={handleEmailChange}
                className={`form-heading-font popup-input ${emailError ? 'error-border' : ''}`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 15px',
                  width: '100%',
                }}
              />
              {emailError && (
                <p className="error-message" style={{ color: 'rgb(255 254 254)' }}>
                  {emailError}
                </p>
              )}
            </div>

            {/* reCAPTCHA */}
            <div
              className="w-100 d-flex justify-content-center"
              style={{
                marginTop: '25px',
                marginBottom: '25px',
                padding: '15px',
                borderRadius: '10px',
              }}
            >
              <ReCAPTCHA
                sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                onChange={handleRecaptchaVerify}
                theme="light"
              />
            </div>

            {/* Submit Button */}
            <div className="w-100 d-flex justify-content-center" style={{ marginTop: '20px' }}>
              <button
                className="submit-btn"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                style={{
                  background: 'transparent',
                  color: 'white',
                  border: '1px solid white',
                  borderRadius: '8px',
                  padding: '12px 30px',
                  fontSize: '1.2rem',
                  fontWeight: '300',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? '0.7' : '1',
                }}
              >
                {isSubmitting ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                      style={{ width: '20px', height: '20px', marginRight: '10px' }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Processing...
                  </>
                ) : (
                  <>
                    Try it Now
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M7 17l9.2-9.2M17 17V7H7"></path>
                    </svg>
                  </>
                )}
              </button>
            </div>

            {/* Success Message */}
            {successMessage && (
              <p style={{ color: '#fff', marginTop: '20px', textAlign: 'center' }}>
                {successMessage}
              </p>
            )}
            <div
              style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center',
                marginTop: '25px',
                borderRadius: '10px',
                marginBottom: '1rem',
              }}
            >
              This site is protected by reCAPTCHA.
              <br />
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white', textDecoration: 'underline' }}
              >
                Privacy Policy
              </a>{' '}
              and
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white', textDecoration: 'underline' }}
              >
                {' '}
                Terms of Service
              </a>{' '}
              apply.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
